// src/services/dataService.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Production-grade Firebase-only data service
if (!db) {
  throw new Error('Firebase not initialized. Check your Firebase configuration.');
}

/**
 * Production Firebase data service with enterprise-grade reliability
 * No localStorage fallbacks - pure Firebase operation for multi-tenant SaaS
 */
class DataService {
  constructor() {
    this.listeners = new Map(); // For real-time listeners
  }

  // Generic CRUD operations with enhanced error handling
  async getAll(collectionName) {
    try {
      return await this._getFirebaseCollection(collectionName);
    } catch (error) {
      console.error(`❌ DataService.getAll(${collectionName}) failed:`, error);
      throw new Error(`Failed to retrieve ${collectionName}: ${error.message}`);
    }
  }

  async getById(collectionName, id) {
    try {
      return await this._getFirebaseDocument(collectionName, id);
    } catch (error) {
      console.error(`❌ DataService.getById(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to retrieve ${collectionName}/${id}: ${error.message}`);
    }
  }

  async create(collectionName, data) {
    try {
      return await this._createFirebaseDocument(collectionName, data);
    } catch (error) {
      console.error(`❌ DataService.create(${collectionName}) failed:`, error);
      throw new Error(`Failed to create ${collectionName}: ${error.message}`);
    }
  }

  async update(collectionName, id, data) {
    try {
      return await this._updateFirebaseDocument(collectionName, id, data);
    } catch (error) {
      console.error(`❌ DataService.update(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to update ${collectionName}/${id}: ${error.message}`);
    }
  }

  async delete(collectionName, id) {
    try {
      console.log(`🗑️ DataService.delete(${collectionName}/${id})`);
      return await this._deleteFirebaseDocument(collectionName, id);
    } catch (error) {
      console.error(`❌ DataService.delete(${collectionName}, ${id}) failed:`, error);
      throw new Error(`Failed to delete ${collectionName}/${id}: ${error.message}`);
    }
  }

  async batchDelete(collectionName, ids) {
    try {
      console.log(`🔥 DataService.batchDelete on ${collectionName} for ${ids.length} docs`);
      return await this._batchDeleteFirebase(collectionName, ids);
    } catch (error) {
      console.error(`❌ DataService.batchDelete(${collectionName}) failed:`, error);
      throw new Error(`Failed to batch delete from ${collectionName}: ${error.message}`);
    }
  }

  async query(collectionName, conditions = [], orderByField = null, orderDirection = 'asc') {
    try {
      return await this._queryFirebase(collectionName, conditions, orderByField, orderDirection);
    } catch (error) {
      console.error(`❌ DataService.query(${collectionName}) failed:`, error);
      throw new Error(`Failed to query ${collectionName}: ${error.message}`);
    }
  }

  // Real-time listeners
  subscribe(collectionName, callback, conditions = []) {
    try {
      return this._subscribeFirebase(collectionName, callback, conditions);
    } catch (error) {
      console.error(`❌ DataService.subscribe(${collectionName}) failed:`, error);
      throw new Error(`Failed to subscribe to ${collectionName}: ${error.message}`);
    }
  }

  unsubscribe(listenerId) {
    if (this.listeners.has(listenerId)) {
      const unsubscribe = this.listeners.get(listenerId);
      unsubscribe();
      this.listeners.delete(listenerId);
    }
  }

  // Firebase implementations
  async _getFirebaseCollection(collectionName) {
    try {
      const querySnapshot = await getDocs(collection(db, collectionName));
      return querySnapshot.docs
        .filter(doc => doc.id !== '_init') // Filter out initialization documents
        .map(doc => {
          const data = doc.data();
          // Use Firebase document ID as the primary ID, ignore any id field in data
          return {
            ...data,
            id: doc.id
          };
        });
    } catch (error) {
      console.error(`Error getting Firebase collection ${collectionName}:`, error);

      // If permission error, try refreshing auth token and retry once
      if (error.code === 'permission-denied' || error.message.includes('insufficient permissions')) {
        console.log('🔄 Retrying with fresh auth token...');
        try {
          // Import auth here to avoid circular dependency
          const { auth } = await import('../config/firebase');
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true); // Force token refresh
            const retrySnapshot = await getDocs(collection(db, collectionName));
            return retrySnapshot.docs
              .filter(doc => doc.id !== '_init') // Filter out initialization documents
              .map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
          }
        } catch (retryError) {
          console.error('Retry failed:', retryError);
        }
      }

      throw error;
    }
  }

  async _getFirebaseDocument(collectionName, id) {
    try {
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error(`Error getting Firebase document ${collectionName}/${id}:`, error);
      throw error;
    }
  }

  async _createFirebaseDocument(collectionName, data) {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      return {
        id: docRef.id,
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error(`Error creating Firebase document in ${collectionName}:`, error);
      throw error;
    }
  }

  async _updateFirebaseDocument(collectionName, id, data) {
    try {
      const docRef = doc(db, collectionName, id);

      // Try update first, if document doesn't exist, create it
      try {
        await updateDoc(docRef, {
          ...data,
          updatedAt: new Date()
        });
      } catch (updateError) {
        if (updateError.code === 'not-found') {
          console.log(`Document ${collectionName}/${id} not found, creating it...`);
          // Use setDoc to create the document with the ID
          await setDoc(docRef, {
            id,
            ...data,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        } else {
          throw updateError;
        }
      }

      return {
        id,
        ...data,
        updatedAt: new Date()
      };
    } catch (error) {
      console.error(`Error updating Firebase document ${collectionName}/${id}:`, error);
      throw error;
    }
  }

  async _deleteFirebaseDocument(collectionName, id) {
    try {
      console.log(`🔥 Firebase Delete: Attempting to delete ${collectionName}/${id}`);

      // First try direct delete with provided ID
      let docRef = doc(db, collectionName, id);
      console.log('Document reference created:', docRef.path);

      try {
        await deleteDoc(docRef);
        console.log(`✅ Firebase Delete: Successfully deleted ${collectionName}/${id}`);
        return true;
      } catch (directDeleteError) {
        console.warn(`⚠️ Direct delete failed for ${id}, trying to find by timestamp ID...`);

        // If direct delete fails, try to find document by timestamp ID field
        const q = query(collection(db, collectionName), where('id', '==', id));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docToDelete = querySnapshot.docs[0];
          console.log(`🎯 Found document with timestamp ID ${id}, Firebase ID: ${docToDelete.id}`);

          await deleteDoc(docToDelete.ref);
          console.log(`✅ Firebase Delete: Successfully deleted ${collectionName}/${docToDelete.id} (timestamp ID: ${id})`);
          return true;
        } else {
          console.error(`❌ No document found with ID ${id}`);
          throw new Error(`Document with ID ${id} not found`);
        }
      }
    } catch (error) {
      console.error(`❌ Firebase Delete Error for ${collectionName}/${id}:`, error);
      throw error;
    }
  }

  async _batchDeleteFirebase(collectionName, ids) {
    if (!ids || ids.length === 0) {
      return 0;
    }
    if (ids.length > 500) {
      throw new Error('Cannot delete more than 500 documents in a single batch.');
    }

    try {
      const batch = writeBatch(db);
      ids.forEach(id => {
        const docRef = doc(db, collectionName, id);
        batch.delete(docRef);
      });
      await batch.commit();
      console.log(`✅ Firebase Batch Delete: Successfully deleted ${ids.length} documents from ${collectionName}`);
      return ids.length;
    } catch (error) {
      console.error(`❌ Firebase Batch Delete Error for ${collectionName}:`, error);
      throw error;
    }
  }

  async _queryFirebase(collectionName, conditions, orderByField, orderDirection) {
    try {
      let q = collection(db, collectionName);

      // Apply where conditions
      conditions.forEach(condition => {
        const [field, operator, value] = condition;
        q = query(q, where(field, operator, value));
      });

      // Apply ordering
      if (orderByField) {
        q = query(q, orderBy(orderByField, orderDirection));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs
        .filter(doc => doc.id !== '_init') // Filter out initialization documents
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
    } catch (error) {
      console.error(`Error querying Firebase collection ${collectionName}:`, error);
      throw error;
    }
  }

  _subscribeFirebase(collectionName, callback, conditions) {
    try {
      let q = collection(db, collectionName);

      // Apply where conditions
      conditions.forEach(condition => {
        const [field, operator, value] = condition;
        q = query(q, where(field, operator, value));
      });

      const listenerId = `${collectionName}_${Date.now()}`;
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const data = querySnapshot.docs
          .filter(doc => doc.id !== '_init') // Filter out initialization documents
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        callback(data);
      });

      this.listeners.set(listenerId, unsubscribe);
      return listenerId;
    } catch (error) {
      console.error(`Error subscribing to Firebase collection ${collectionName}:`, error);
      throw error;
    }
  }

  // Production utility methods
  isFirebaseConnected() {
    return !!db;
  }

  getConnectionStatus() {
    return {
      firebase: !!db,
      timestamp: new Date().toISOString()
    };
  }


  // Production utility methods
  getDataSource() {
    return 'firebase';
  }
}

// Export singleton instance
const dataService = new DataService();
export { dataService };
export default dataService;
